import { logger } from "@/lib/logger";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ChevronDown, ChevronRight, Loader2, ExternalLink,
  Layers, LayoutList, FileText, Archive,
} from "lucide-react";
import { FaMeta, FaMicrosoft, FaLinkedin } from "react-icons/fa6";
import { FcGoogle } from "react-icons/fc";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
function authHeaders() { const t = localStorage.getItem("roas_session_token"); return t ? { "Authorization": `Bearer ${t}` } : {}; }

const PLATFORM_LABELS = {
  google: { name: "Ad Group", icon: <FcGoogle className="w-4 h-4" /> },
  meta: { name: "Ad Set", icon: <FaMeta className="w-4 h-4 text-[#1877F2]" /> },
  linkedin: { name: "Campaign", icon: <FaLinkedin className="w-4 h-4 text-[#0A66C2]" /> },
  bing: { name: "Ad Group", icon: <FaMicrosoft className="w-4 h-4 text-[#00A4EF]" /> },
};

const StatusBadge = ({ status }) => {
  const colors = {
    active: "bg-emerald-500/10 text-emerald-400",
    enabled: "bg-emerald-500/10 text-emerald-400",
    paused: "bg-amber-500/10 text-amber-400",
    removed: "bg-red-500/10 text-red-400",
  };
  return <Badge className={cn("text-xs capitalize", colors[status] || "bg-zinc-500/10 text-zinc-400")}>{status}</Badge>;
};

const Metric = ({ label, value, isCurrency, isPercent }) => (
  <div className="text-center px-2">
    <p className="text-zinc-400 text-xs">{label}</p>
    <p className="text-white text-sm font-medium tabular-nums">
      {isCurrency ? `$${(value || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : isPercent ? `${(value || 0).toFixed(2)}%` : (value || 0).toLocaleString()}
    </p>
  </div>
);

function AdRow({ ad }) {
  return (
    <div className="flex items-center justify-between py-2.5 px-4 ml-16 border-l border-white/5 bg-white/[0.02] rounded-r-lg">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <FileText className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" />
        <span className="text-zinc-300 text-sm truncate">{ad.name}</span>
        <StatusBadge status={ad.status} />
        {ad.ad_type && <Badge className="bg-zinc-700/50 text-zinc-400 text-xs">{ad.ad_type}</Badge>}
      </div>
      <div className="flex items-center gap-1">
        <Metric label="Spend" value={ad.spend} isCurrency />
        <Metric label="Impr." value={ad.impressions} />
        <Metric label="Clicks" value={ad.clicks} />
        <Metric label="CTR" value={ad.ctr} isPercent />
        <Metric label="Conv." value={ad.conversions} />
      </div>
    </div>
  );
}

function AdGroupRow({ adGroup, platform, campaignId }) {
  const [expanded, setExpanded] = useState(false);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const label = PLATFORM_LABELS[platform]?.name || "Ad Group";

  const loadAds = async () => {
    if (loaded) { setExpanded(!expanded); return; }
    setLoading(true);
    try {
      const resp = await fetch(`${API}/ad-groups/${adGroup.ad_group_id}/ads`, { credentials: "include", headers: authHeaders() });
      if (resp.ok) { setAds(await resp.json()); setLoaded(true); }
    } catch (e) { logger.error("Failed to load ads:", e); }
    finally { setLoading(false); setExpanded(true); }
  };

  return (
    <div>
      <div
        onClick={loadAds}
        className="flex items-center justify-between py-2.5 px-4 ml-8 border-l border-white/10 hover:bg-white/[0.03] cursor-pointer rounded-r-lg transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin text-zinc-400" /> : expanded ? <ChevronDown className="w-3.5 h-3.5 text-zinc-400" /> : <ChevronRight className="w-3.5 h-3.5 text-zinc-400" />}
          <LayoutList className="w-3.5 h-3.5 text-violet-400 flex-shrink-0" />
          <span className="text-zinc-200 text-sm truncate">{adGroup.name}</span>
          <StatusBadge status={adGroup.status} />
          <span className="text-zinc-500 text-xs">{label}</span>
        </div>
        <div className="flex items-center gap-1">
          <Metric label="Spend" value={adGroup.spend} isCurrency />
          <Metric label="Impr." value={adGroup.impressions} />
          <Metric label="Clicks" value={adGroup.clicks} />
          <Metric label="CTR" value={adGroup.ctr} isPercent />
          <Metric label="ROAS" value={adGroup.roas} />
        </div>
      </div>
      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="space-y-0.5 py-1">
              {ads.length === 0 && loaded && <p className="text-zinc-500 text-xs ml-20 py-2">No ads in this {label.toLowerCase()}</p>}
              {ads.map((ad) => <AdRow key={ad.ad_id || ad.external_id} ad={ad} />)}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function CampaignHierarchy({ campaign, onArchive }) {
  const [expanded, setExpanded] = useState(false);
  const [adGroups, setAdGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const platform = campaign.platform;
  const groupLabel = PLATFORM_LABELS[platform]?.name || "Ad Group";

  const loadAdGroups = async () => {
    if (loaded) { setExpanded(!expanded); return; }
    setLoading(true);
    try {
      const resp = await fetch(`${API}/campaigns/${campaign.campaign_id}/ad-groups`, { credentials: "include", headers: authHeaders() });
      if (resp.ok) { setAdGroups(await resp.json()); setLoaded(true); }
    } catch (e) { logger.error("Failed to load ad groups:", e); }
    finally { setLoading(false); setExpanded(true); }
  };

  const spend = campaign.total_spend || 0;
  const revenue = campaign.revenue || 0;
  const roas = spend > 0 ? (revenue / spend).toFixed(2) : "0.00";

  return (
    <div data-testid={`campaign-row-${campaign.campaign_id}`} className="border border-white/10 rounded-xl overflow-hidden bg-[#121212] mb-2">
      {/* Campaign Row */}
      <div
        onClick={loadAdGroups}
        className="flex items-center justify-between p-4 hover:bg-white/[0.02] cursor-pointer transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {loading ? <Loader2 className="w-4 h-4 animate-spin text-zinc-400" /> : expanded ? <ChevronDown className="w-4 h-4 text-zinc-400" /> : <ChevronRight className="w-4 h-4 text-zinc-400" />}
          <Layers className="w-4 h-4 text-blue-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-white font-medium text-sm truncate">{campaign.name}</span>
              <StatusBadge status={campaign.status} />
              {PLATFORM_LABELS[platform]?.icon}
            </div>
            {campaign.channel_type && <span className="text-zinc-500 text-xs capitalize">{campaign.channel_type?.toLowerCase().replace(/_/g, " ")}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Metric label="Budget/day" value={campaign.daily_budget} isCurrency />
          <Metric label="Spend" value={spend} isCurrency />
          <Metric label="Revenue" value={revenue} isCurrency />
          <Metric label="ROAS" value={parseFloat(roas)} />
          <Metric label="Impr." value={campaign.impressions} />
          <Metric label="Clicks" value={campaign.clicks} />
          <Metric label="Conv." value={campaign.conversions} />
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => { e.stopPropagation(); onArchive?.(campaign.campaign_id); }}
            className="text-zinc-400 hover:text-amber-400 hover:bg-amber-500/10 ml-2"
            title="Archive campaign"
          >
            <Archive className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Ad Groups */}
      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden border-t border-white/5">
            <div className="py-1 space-y-0.5">
              {adGroups.length === 0 && loaded && <p className="text-zinc-500 text-xs ml-12 py-3">No {groupLabel.toLowerCase()}s in this campaign</p>}
              {adGroups.map((ag) => (
                <AdGroupRow key={ag.ad_group_id || ag.external_id} adGroup={ag} platform={platform} campaignId={campaign.campaign_id} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
